// Kontrakt edycji zapisanego filtra klienta i jego ustawień raportowania.
//
// Kształt `SavedFilter` jest DOKŁADNIE tym, co zapisuje aplikacja Flutter
// (`AuctionFilters.toJson`) — z jedną różnicą, którą backend wymusza przy zapisie:
// kryteria skalarne (przełączniki, budżety, daty) idą jako skalary, nie jako
// jednoelementowe tablice. Powód w `domain::admin::filter_edit`: wyszukiwarka
// deserializuje filtr do `SearchQuery` bez rozpakowywania tablic i przy niezgodności typu
// zwija CAŁY filtr do samego zapytania tekstowego.

import type { FiltersDisplay } from "./filters";

export type SavedFilter = {
  // Wewnętrzna, sklejona nowymi liniami reprezentacja fraz (tak trzyma je front).
  search_text: string;
  // Pierwszoklasowa tablica fraz — OR między frazami.
  queries: string[];
  excluded_phrases?: string[];
  // Kryteria. Listy jako `string[]`, przełączniki jako `true` (brak klucza = wyłączony),
  // budżety jako liczby, daty jako stringi ISO-8601.
  filters: Record<string, unknown>;
  sort: string;
  direction: string;
  limit: number;
};

// Kanał typu powiadomienia właściciela (`UserNotificationPreferences` z wartością domyślną
// z `NotificationTypeDelivery`).
export type NotificationChannelPreference = {
  type: string;
  method: string;
  enabled: boolean;
};

// Ustawienia powiadomień filtra: interwały właściciela (`UserFilterPreferences`) i — w
// szczegółach filtra — jego kanały per typ powiadomienia (te są per osoba, nie per filtr).
export type NotificationSubscription = {
  intervals: string[];
  channels?: NotificationChannelPreference[];
};

// Odpowiedź na pytanie „czemu klient nic nie dostaje?”.
export type DeliveryContext = {
  employee_found: boolean;
  newsletter: boolean;
  // `ContactData.telephone_number`. Worker wysyła SMS-y TU, nie na `auth.users.phone`.
  phone: string | null;
  suppressions?: Array<{ channel: string; reason: string; created_at: string }>;
};

export type AdminFilterDetail = {
  id: string;
  name: string | null;
  public: boolean | null;
  created_by: string;
  created_at: string | null;
  modified_at: string | null;
  filter: SavedFilter | null;
  filters_display: FiltersDisplay | null;
  notification: NotificationSubscription | null;
  delivery: DeliveryContext | null;
};

// Słowniki edytora — jedno źródło prawdy po stronie Rusta (`GET /admin/filter-dictionaries`),
// żeby dopisanie branży w `domain::industries` nie wymagało edycji TypeScriptu.
export type FilterDictionaries = {
  industries: Array<{ slug: string; name: string }>;
  voivodeships: string[];
  sort_fields: string[];
  sort_directions: string[];
  list_keys: string[];
  bool_keys: string[];
  number_keys: string[];
  date_keys: string[];
  // Kanały typu powiadomienia: `email | phone | push | app`.
  notification_channels: string[];
  // `FilterPreference`: `instantly | daily | weekly | monthly`.
  send_intervals: string[];
  max_search_phrases: number;
  max_filter_limit: number;
};

export const EMPTY_SAVED_FILTER: SavedFilter = {
  search_text: "",
  queries: [],
  excluded_phrases: [],
  filters: {},
  sort: "_score",
  direction: "desc",
  limit: 25,
};

export const SEND_INTERVAL_LABELS: Record<string, string> = {
  instantly: "Na bieżąco (wpis od razu, mail co godzinę)",
  daily: "Codziennie (raport o 6:00)",
  weekly: "Co tydzień (poniedziałek 6:00)",
  monthly: "Co miesiąc (1. dnia 6:00)",
};

export const CHANNEL_LABELS: Record<string, string> = {
  email: "E-mail",
  sms: "SMS",
  phone: "SMS",
  push: "Push",
  app: "Aplikacja",
};

// Typy powiadomień (slug `NotificationType` w Supabase), które tworzy potok.
export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  daily_raport: "Raport dzienny",
  weekly_raport: "Raport tygodniowy",
  monthly_raport: "Raport miesięczny",
  instant_raport: "Nowe przetargi na bieżąco",
  instant_auction_update: "Zmiana obserwowanego przetargu",
  new_note: "Nowa notatka",
  update_note: "Zmiana notatki",
  problem: "Problem z powiadomieniami",
};

export const SORT_FIELD_LABELS: Record<string, string> = {
  _score: "Trafność / najnowsze",
  submitting_date: "Termin składania",
  id: "ID ogłoszenia",
  subject: "Przedmiot",
};

// Etykiety kryteriów — pisownia klucza jest tą, którą zapisuje aplikacja.
export const FILTER_KEY_LABELS: Record<string, string> = {
  industries: "Branże",
  cpv_code: "Kody CPV",
  states: "Województwa",
  districts: "Powiaty",
  cities: "Miasta",
  country: "Kraje",
  organizer_name: "Organizatorzy",
  source: "Źródła",
  category: "Kategorie",
  form: "Formy",
  branches: "Branże (stara taksonomia)",
  status: "Status (lista klienta)",
  auction_type: "Typ ogłoszenia",
  emails: "E-maile kontaktowe",
  phones: "Telefony kontaktowe",
  id: "ID ogłoszeń",
  wadium: "Tylko z wadium",
  attachments: "Tylko z załącznikami",
  original_link: "Tylko z linkiem źródłowym",
  hide_untranslated: "Ukryj nieprzetłumaczone",
  budget_min: "Budżet od",
  budget_max: "Budżet do",
  created_at_min: "Dodano od",
  created_at_max: "Dodano do",
  submitting_date_min: "Składanie od",
  submitting_date_max: "Składanie do",
};
